/**
 * CKEditor 5 커스텀 설정
 * - 무료 오픈소스 기능만 사용 (GPL 라이선스)
 * - 문서 작성에 필요한 기본 플러그인 포함
 */
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading,
  Font,
  Alignment,
  List,
  Indent,
  IndentBlock,
  Link,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  BlockQuote,
  HorizontalLine,
  FindAndReplace,
  PasteFromOffice,
  Undo,
  GeneralHtmlSupport,
  SourceEditing, // 소스 편집 모드 (외부 HTML 확인/수정용)
  // 이미지 관련 플러그인
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  Base64UploadAdapter,
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

/**
 * CKEditor 플러그인 목록
 */
export const editorPlugins = [
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading,
  Font,
  Alignment,
  List,
  Indent,
  IndentBlock,
  Link,
  // GeneralHtmlSupport를 Table보다 먼저 로드하여 외부 HTML 테이블을 보존
  GeneralHtmlSupport,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  BlockQuote,
  HorizontalLine,
  FindAndReplace,
  PasteFromOffice,
  Undo,
  SourceEditing, // 소스 편집 모드 (외부 HTML 확인/수정용)
  // 이미지 관련 플러그인
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  Base64UploadAdapter,
];

/**
 * CKEditor 기본 설정
 */
export const editorConfig = {
  licenseKey: 'GPL', // GPL 오픈소스 라이선스 사용 (무료)
  plugins: editorPlugins,
  toolbar: {
    items: [
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'insertImage',
      'insertTable',
      'blockQuote',
      'horizontalLine',
      '|',
      'findAndReplace',
      '|',
      'sourceEditing', // 소스 편집 모드 (외부 HTML 확인/수정용)
      '|',
      'undo',
      'redo',
    ],
    shouldNotGroupWhenFull: true,
  },
  heading: {
    options: [
      { model: 'paragraph', title: '본문', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: '제목 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: '제목 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: '제목 3', class: 'ck-heading_heading3' },
    ],
  },
  fontSize: {
    options: [9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32],
    supportAllValues: true,
  },
  // 들여쓰기 설정 (일반 문단에서도 동작하도록)
  indentBlock: {
    offset: 40,
    unit: 'px',
  },
  fontFamily: {
    options: [
      'default',
      '맑은 고딕, Malgun Gothic, sans-serif',
      '굴림, Gulim, sans-serif',
      '돋움, Dotum, sans-serif',
      '바탕, Batang, serif',
      '궁서, Gungsuh, serif',
      'Arial, Helvetica, sans-serif',
      'Georgia, serif',
      'Times New Roman, Times, serif',
      'Verdana, Geneva, sans-serif',
    ],
    supportAllValues: true,
  },
  table: {
    // 외부 HTML 테이블을 GeneralHtmlSupport로 처리하도록 설정
    // Table 플러그인이 외부 테이블을 변환하지 않도록 함
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
    ],
    // 기본 정렬을 좌측으로 설정
    defaultAlignment: 'left',
    // 외부 테이블을 content 타입으로 처리 (layout이 아닌)
    // 이렇게 하면 GeneralHtmlSupport가 테이블을 그대로 보존
    tableLayout: {
      preferredExternalTableType: 'content',
    },
    tableProperties: {
      defaultAlignment: 'left', // 테이블 속성 기본값도 좌측 정렬
      borderColors: [
        { color: '#000000', label: '검정' },
        { color: '#333333', label: '짙은 회색' },
        { color: '#666666', label: '회색' },
        { color: '#999999', label: '밝은 회색' },
        { color: '#CCCCCC', label: '연한 회색' },
        { color: '#FFFFFF', label: '흰색' },
      ],
      backgroundColors: [
        { color: '#FFFFFF', label: '흰색' },
        { color: '#F5F5F5', label: '밝은 회색' },
        { color: '#E0E0E0', label: '회색' },
        { color: '#FFEB3B', label: '노랑' },
        { color: '#FFC107', label: '주황' },
        { color: '#FF5722', label: '빨강' },
        { color: '#4CAF50', label: '녹색' },
        { color: '#2196F3', label: '파랑' },
      ],
    },
    tableCellProperties: {
      borderColors: [
        { color: '#000000', label: '검정' },
        { color: '#333333', label: '짙은 회색' },
        { color: '#666666', label: '회색' },
        { color: '#999999', label: '밝은 회색' },
        { color: '#CCCCCC', label: '연한 회색' },
        { color: '#FFFFFF', label: '흰색' },
      ],
      backgroundColors: [
        { color: '#FFFFFF', label: '흰색' },
        { color: '#F5F5F5', label: '밝은 회색' },
        { color: '#E0E0E0', label: '회색' },
        { color: '#FFEB3B', label: '노랑' },
        { color: '#FFC107', label: '주황' },
        { color: '#FF5722', label: '빨강' },
        { color: '#4CAF50', label: '녹색' },
        { color: '#2196F3', label: '파랑' },
      ],
    },
  },
  language: 'ko',
  // 이미지 설정
  image: {
    // 이미지 툴바 (이미지 선택 시 표시)
    toolbar: [
      'imageStyle:inline',
      'imageStyle:block',
      'imageStyle:side',
      '|',
      'toggleImageCaption',
      'imageTextAlternative',
      '|',
      'resizeImage',
    ],
    // 이미지 삽입 방식
    insert: {
      type: 'auto',
    },
    // 이미지 리사이즈 옵션
    resizeOptions: [
      {
        name: 'resizeImage:original',
        value: null,
        label: '원본 크기',
      },
      {
        name: 'resizeImage:25',
        value: '25',
        label: '25%',
      },
      {
        name: 'resizeImage:50',
        value: '50',
        label: '50%',
      },
      {
        name: 'resizeImage:75',
        value: '75',
        label: '75%',
      },
    ],
  },
  // LibreOffice에서 생성된 HTML의 레거시 태그 지원
  // General HTML Support (GHS) 설정 - 외부 HTML 테이블 보존
  // 중요: Table 플러그인과 충돌 방지를 위해 테이블을 GeneralHtmlSupport로만 처리
  htmlSupport: {
    // Table 플러그인이 외부 테이블을 변환하지 않도록 disallow 설정
    // GeneralHtmlSupport가 테이블을 그대로 보존하도록 함
    disallow: [
      // Table 플러그인의 기본 테이블 변환을 비활성화하지 않음
      // 대신 allow에서 명시적으로 허용하여 GeneralHtmlSupport가 우선 처리하도록 함
    ],
    allow: [
      // 테이블 관련 태그 - 모든 속성 허용 (colspan, rowspan 등)
      // 제공된 정보와 동일한 패턴 사용 (table, thead, tbody, tr, th, td)
      // styles: true로 설정하여 모든 스타일 허용 (겹침 방지)
      {
        name: /^(table|thead|tbody|tr|th|td)$/,
        attributes: true, // 모든 속성 허용 (cellpadding, cellspacing, colspan, rowspan 등)
        classes: true, // 모든 클래스 허용
        styles: true, // 모든 스타일 허용 (겹침 방지를 위해 true로 변경)
      },
      // tfoot, col, colgroup도 추가 지원
      {
        name: /^(tfoot|col|colgroup)$/,
        attributes: true,
        classes: true,
        styles: true, // 모든 스타일 허용
      },
      // font 태그 (LibreOffice에서 사용)
      {
        name: 'font',
        attributes: ['face', 'size', 'color'],
        classes: true,
        styles: true,
      },
      // dl, dt, dd 태그 (LibreOffice 들여쓰기)
      {
        name: /^(dl|dt|dd)$/,
        attributes: true,
        classes: true,
        styles: true,
      },
      // 기타 레거시 태그
      {
        name: /^(div|span|p|br|hr)$/,
        attributes: true,
        classes: true,
        styles: true,
      },
      // 제목 태그 (h1-h6)
      {
        name: /^(h[1-6])$/,
        attributes: true,
        classes: true,
        styles: true,
      },
      // 리스트 태그
      {
        name: /^(ul|ol|li)$/,
        attributes: true,
        classes: true,
        styles: true,
      },
      // 링크 태그
      {
        name: 'a',
        attributes: ['href', 'target', 'rel', 'title'],
        classes: true,
        styles: true,
      },
      // 이미지 태그 (Base64 포함)
      {
        name: 'img',
        attributes: ['src', 'alt', 'width', 'height'],
        classes: true,
        styles: true,
      },
      // 스타일 속성 허용 (모든 태그에 공통 적용)
      {
        name: /.*/,
        attributes: [
          'style',
          'align',
          'valign',
          'bgcolor',
          'border',
          'cellpadding',
          'cellspacing',
          'width',
          'height',
          'id',
          'class',
          'title',
        ],
      },
    ],
  },
};

/**
 * ClassicEditor 내보내기
 */
export { ClassicEditor };

export default ClassicEditor;
